import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import type { DocumentTemplate } from '../api/types';
import { ErrorMessage } from '../components/ErrorMessage';
import { Modal } from '../components/Modal';
import { MutationError } from '../components/MutationError';
import { fmtDateTime } from '../ops/format';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { ContractConfiguratorModal } from './ContractConfiguratorModal';
import { categoryLabels, categoryOrder, statusLabels } from './labels';

// DocumentsPanel: Dokumente zu einem Lead (am Lead und am Termin). Neue Dokumente entstehen aus Vorlagen und sind
// mit Lead-, Makler- und Termindaten vorbelegt.
export function DocumentsPanel({ leadId, appointmentId, embedded = false }: { leadId: string; appointmentId?: string; embedded?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const [picking, setPicking] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const docs = useQuery({ queryKey: projectKeys.leadDocuments(project.id, leadId), queryFn: ({ signal }) => papi.leadDocuments(leadId, signal) });
  const base = `/p/${project.key}`;
  return (
    <section className={embedded ? 'doc-area-row documents-panel' : 'card documents-panel'} aria-labelledby={`docs-${leadId}`}>
      <div className="section-head">
        {embedded ? <h3 id={`docs-${leadId}`}>Dokumente</h3> : <h2 id={`docs-${leadId}`}>Dokumente</h2>}
        {can('document.edit') && (
          <div className="form-actions">
            <button type="button" className="btn btn-primary btn-small" onClick={() => setConfiguring(true)}>
              Auftrags-Konfigurator (Slider)
            </button>
            <button type="button" className="btn btn-small" onClick={() => setPicking(true)}>
              Neues Dokument
            </button>
          </div>
        )}
      </div>
      {docs.isError && <ErrorMessage error={docs.error} onRetry={() => void docs.refetch()} />}
      {docs.data && docs.data.items.length === 0 && <p className="muted">Noch keine Dokumente. Verträge, Nachweise und Unterlagenlisten entstehen aus Vorlagen.</p>}
      {docs.data && docs.data.items.length > 0 && (
        <ul className="plain-list documents-list">
          {docs.data.items.map((d) => (
            <li key={d.id}>
              <Link to={`${base}/dokumente/${d.id}`}>{d.title}</Link>{' '}
              <span className={d.status === 'FINAL' ? 'badge badge-ok' : 'badge badge-muted'}>{statusLabels[d.status] ?? d.status}</span>
              {d.approval_status === 'PENDING_APPROVAL' && (
                <>
                  {' '}
                  <span className="badge badge-warn">Freigabe offen</span>
                </>
              )}
              {d.approval_status === 'APPROVED' && (
                <>
                  {' '}
                  <span className="badge badge-ok">Freigegeben</span>
                </>
              )}
              <span className="muted">
                {' '}
                · {fmtDateTime(d.updated_at)}
                {d.created_by_name ? ` · ${d.created_by_name}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      {picking && <TemplatePicker leadId={leadId} appointmentId={appointmentId} onClose={() => setPicking(false)} />}
      {configuring && <ContractConfiguratorModal leadId={leadId} appointmentId={appointmentId} onClose={() => setConfiguring(false)} />}
    </section>
  );
}

function TemplatePicker({ leadId, appointmentId, onClose }: { leadId: string; appointmentId?: string; onClose: () => void }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const templates = useQuery({ queryKey: projectKeys.documentTemplates(project.id), queryFn: ({ signal }) => papi.documentTemplates(false, signal) });
  const create = useMutation({
    mutationFn: (t: DocumentTemplate) => papi.createLeadDocument(leadId, { template_id: t.id, appointment_id: appointmentId ?? null }),
    onSuccess: (d) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.leadDocuments(project.id, leadId) });
      navigate(`/p/${project.key}/dokumente/${d.id}`);
    },
  });
  const groups = categoryOrder
    .map((c) => ({ category: c, items: (templates.data?.items ?? []).filter((t) => t.category === c) }))
    .filter((g) => g.items.length > 0);
  return (
    <Modal title="Neues Dokument" size="wide" onClose={onClose} actions={<button type="button" className="btn" onClick={onClose}>Abbrechen</button>}>
      {templates.isError && <ErrorMessage error={templates.error} onRetry={() => void templates.refetch()} />}
      {templates.isPending && <p className="muted">Vorlagen werden geladen …</p>}
      {create.isError && <MutationError error={create.error} />}
      <div className="template-groups">
        {groups.map((g) => (
          <section key={g.category} aria-label={categoryLabels[g.category]}>
            <h3>{categoryLabels[g.category]}</h3>
            <ul className="template-list">
              {g.items.map((t) => (
                <li key={t.id}>
                  <button type="button" className="template-choice" disabled={create.isPending} onClick={() => create.mutate(t)}>
                    <strong>{t.name}</strong>
                    {t.variant && <span className="template-variant">{t.variant}</span>}
                    {t.description && <small className="muted">{t.description}</small>}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  );
}
